import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import { Plus, Trash2, Edit2 } from "lucide-react";
import {
  fetchMarketingWhatsAppLogs,
  createMarketingWhatsAppLog,
  updateMarketingWhatsAppLog,
  deleteMarketingWhatsAppLog,
  fetchMarketingContacts,
} from "../../../../../../redux/actions/marketingAction";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const STATUS_COLORS = {
  sent: "default",
  delivered: "info",
  read: "success",
  replied: "primary",
  pending: "warning",
};

const WhatsAppLog = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { whatsappLogs, contacts } = useSelector((state) => state.marketing);
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    contact_id: "", message: "", status: "sent", notes: "",
  });

  useEffect(() => {
    dispatch(fetchMarketingWhatsAppLogs({ page: page + 1 }));
    dispatch(fetchMarketingContacts({ page_size: 100 }));
  }, [dispatch, page]);

  const handleOpen = (log = null) => {
    setEditingLog(log);
    setFormData({
      contact_id: log?.contact_id || "",
      message: log?.message || "",
      status: log?.status || "sent",
      notes: log?.notes || "",
    });
    setFormOpen(true);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      if (editingLog) {
        await dispatch(updateMarketingWhatsAppLog(editingLog.id, { status: formData.status, notes: formData.notes, message: formData.message }));
      } else {
        await dispatch(createMarketingWhatsAppLog(formData));
      }
      setFormOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const items = whatsappLogs?.items || [];
  const total = whatsappLogs?.total || 0;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t('mkt_whatsapp_log')} ({total})
        </Typography>
        <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => handleOpen()} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}>
          {t('mkt_log_message')}
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.divider, 0.3) }}>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_contact')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_message')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_sent_at')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_notes')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>{t('mkt_no_whatsapp_logs')}</TableCell></TableRow>
              ) : items.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{log.contact_email || "—"}</Typography>
                    {log.contact_name && <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{log.contact_name}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{log.message || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={log.status} size="small" color={STATUS_COLORS[log.status] || "default"} sx={{ borderRadius: "6px", fontSize: "0.7rem", fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{new Date(log.sent_at).toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>{log.notes || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(log)}><Edit2 size={14} /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteTarget(log)}><Trash2 size={14} color={theme.palette.error?.main} /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={25} rowsPerPageOptions={[25]} />}
      </Paper>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editingLog ? t('mkt_edit_log') : t('mkt_log_whatsapp_message')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {!editingLog && (
              <FormControl size="small" fullWidth>
                <InputLabel>Contact</InputLabel>
                <Select value={formData.contact_id} onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })} label="Contact">
                  {(contacts?.items || []).map((c) => <MenuItem key={c.id} value={c.id}>{c.email} {c.first_name ? `(${c.first_name})` : ""}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField label="Message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} fullWidth size="small" multiline rows={3} />
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} label="Status">
                {["pending", "sent", "delivered", "read", "replied"].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} fullWidth size="small" multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)}>{t('common_cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={(!editingLog && !formData.contact_id) || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? t('mkt_saving') : t('common_save')}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await dispatch(deleteMarketingWhatsAppLog(deleteTarget.id));
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.contact?.email || t("mkt_log")}
        itemType={t("mkt_log")}
      />
    </Box>
  );
};

export default WhatsAppLog;
